import React, { useEffect, lazy, Suspense, Component, ErrorInfo, ReactNode } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { createTheme, ThemeProvider, CssBaseline, CircularProgress, LinearProgress, Box, Typography } from '@mui/material';
import { AuthProvider } from './context/AuthContext';
import { SettingsProvider } from './context/SettingsContext';
import PrivateRoute from './components/PrivateRoute';
import PWAInstallPrompt from './components/PWAInstallPrompt';
import NetworkStatus from './components/NetworkStatus';
import UpdateNotification from './components/UpdateNotification';
import Login from './pages/Login';
import { SnackbarProvider } from 'notistack';
import './App.css';
import MainLayout from './components/MainLayout';

// Lazy load page components with retry mechanism to reduce initial bundle size
const createLazyComponent = (importFunc: () => Promise<any>) => {
  return lazy(() =>
    importFunc().catch((error) => {
      console.warn('Initial lazy load failed, retrying...', error);
      // Retry the import after a small delay if it fails
      return new Promise((resolve, reject) => {
        setTimeout(() => {
          importFunc()
            .then(resolve)
            .catch((retryError) => {
              console.error('Lazy load retry failed:', retryError);
              // Try one more time after clearing any cached failures
              setTimeout(() => {
                importFunc().then(resolve).catch(reject);
              }, 200);
            });
        }, 100);
      });
    })
  );
};

const Inventory = createLazyComponent(() => import('./pages/Inventory'));
const Services = createLazyComponent(() => import('./pages/Services'));
const Packages = createLazyComponent(() => import('./pages/Packages'));
const POS = createLazyComponent(() => import('./pages/POS'));
const Customers = createLazyComponent(() => import('./pages/Customers'));
const CustomerProfilePage = createLazyComponent(() => import('./pages/CustomerProfilePage'));
const Finances = createLazyComponent(() => import('./pages/Finances'));
const Settings = createLazyComponent(() => import('./pages/Settings'));
const ServiceSlots = createLazyComponent(() => import('./pages/ServiceSlots'));
const PhotoUploadPage = createLazyComponent(() => import('./pages/PhotoUploadPage'));
const Appointments = createLazyComponent(() => import('./pages/Appointments'));
const Reports = createLazyComponent(() => import('./pages/Reports'));
const GeneralLedger = createLazyComponent(() => import('./pages/GeneralLedger'));
const WhatsApp = createLazyComponent(() => import('./pages/WhatsApp'));
const WhatsAppTest = createLazyComponent(() => import('./pages/WhatsAppTest'));
const NotificationSettings = createLazyComponent(() => import('./pages/NotificationSettings'));
const PublicAppointmentCreate = createLazyComponent(() => import('./pages/PublicAppointmentCreate'));

// Improved loading component with progress bar
const LoadingFallback = () => (
  <Box 
    sx={{ 
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      display: 'flex', 
      flexDirection: 'column',
      alignItems: 'center', 
      justifyContent: 'center',
      bgcolor: 'rgba(255, 255, 255, 0.8)',
      zIndex: 9999
    }}
  >
    <CircularProgress size={40} thickness={4} sx={{ mb: 2 }} />
    <Typography variant="body1" color="text.secondary">
      Loading content...
    </Typography>
    <Box sx={{ width: '200px', mt: 2 }}>
      <LinearProgress color="primary" />
    </Box>
  </Box>
);

// Error Boundary for lazy loaded components
interface ErrorBoundaryState {
  hasError: boolean;
  error?: Error;
}

class LazyLoadErrorBoundary extends Component<
  { children: ReactNode },
  ErrorBoundaryState
> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Lazy loading error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'rgba(255, 255, 255, 0.95)',
            zIndex: 9999
          }}
        >
          <Typography variant="h6" color="error" sx={{ mb: 2 }}>
            Loading Error
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3, textAlign: 'center' }}>
            There was a problem loading this page. Please try refreshing.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <button
              onClick={() => window.location.reload()}
              style={{
                padding: '8px 16px',
                backgroundColor: '#333333',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              Refresh Page
            </button>
            <button
              onClick={() => this.setState({ hasError: false })}
              style={{
                padding: '8px 16px',
                backgroundColor: '#ffffff',
                color: '#333333',
                border: '1px solid #ccc',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              Try Again
            </button>
          </Box>
        </Box>
      );
    }

    return this.props.children;
  }
}

// Create a theme
const theme = createTheme({
  palette: {
    primary: {
      main: '#333333', // Dark gray instead of purple
      light: '#555555',
      dark: '#222222',
    },
    secondary: {
      main: '#3f51b5', // Blue accent color
    },
    background: {
      default: '#f8f8f8', // Light gray background
      paper: '#ffffff',
    },
    text: {
      primary: '#333333',
      secondary: '#666666',
    },
  },
  typography: {
    fontFamily: [
      'Roboto',
      'Arial',
      'sans-serif',
    ].join(','),
    // Reduce font sizes to be more compact on tablet displays
    h4: {
      fontWeight: 600,
      fontSize: '1.75rem', // Smaller than default 2.125rem
    },
    h5: {
      fontSize: '1.4rem', // Smaller than default 1.5rem
    },
    h6: {
      fontWeight: 600,
      fontSize: '1.15rem', // Smaller than default 1.25rem
    },
    body1: {
      fontSize: '0.875rem', // Smaller than default 1rem
    },
    body2: {
      fontSize: '0.8rem', // Smaller than default 0.875rem
    },
    button: {
      fontSize: '0.8rem', // Smaller buttons text
      textTransform: 'none', // Avoid ALL CAPS for better readability
    },
  },
  components: {
    // Fix keyboard size issues on iOS PWA mode
    MuiInputBase: {
      styleOverrides: {
        input: {
          // Set consistent font size to prevent iOS zoom on focus
          fontSize: '16px',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiInputBase-input': {
            fontSize: '16px',
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        input: {
          fontSize: '16px',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.05)',
        },
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          padding: '12px', // Smaller padding (default is 16px)
          '&:last-child': {
            paddingBottom: '12px', // Override default last-child padding
          },
        },
      },
    },
    MuiListItem: {
      styleOverrides: {
        root: {
          paddingTop: '6px', // Reduce padding
          paddingBottom: '6px',
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        sizeSmall: {
          padding: '4px', // More compact icon buttons
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        sizeSmall: {
          height: '22px', // Smaller height (default is 24px)
          fontSize: '0.7rem', // Smaller font
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          padding: '8px 10px', // More compact table cells (default is 16px)
        },
        sizeSmall: {
          padding: '4px 8px', // Even more compact for small tables
        }
      }
    }
  },
  // Add responsive breakpoints optimized for iPad
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 960,
      lg: 1280,
      xl: 1920,
    },
  },
});

// Component to preload common routes and handle development HMR issues
const RoutePreloader = () => {
  useEffect(() => {
    // Clear any webpack module cache in development to prevent lazy load issues
    if (process.env.NODE_ENV === 'development') {
      // Force clear module cache on hot reload
      const clearModuleCache = () => {
        if ((module as any).hot) {
          console.log('Development: Clearing module cache for lazy components');
        }
      };
      clearModuleCache();
    }

    // Preload most commonly used pages after initial render
    const preloadRoutes = async () => {
      const timeout = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

      // Start preloading immediately but spread out the load
      try {
        // POS and Appointments are likely most frequent
        import('./pages/POS');
        await timeout(300);

        import('./pages/Appointments');
        await timeout(300);

        import('./pages/Customers');
        await timeout(300);

        // Other pages can load later when needed
      } catch (err) {
        console.error('Error preloading routes:', err);
      }
    };

    // Start preloading after a short delay to not compete with initial render
    const timer = setTimeout(preloadRoutes, 2000);
    return () => clearTimeout(timer);
  }, []);

  return null;
};

// Wrapper component for lazy routes with error handling
const LazyRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <LazyLoadErrorBoundary>
    <Suspense fallback={<LoadingFallback />}>
      {children}
    </Suspense>
  </LazyLoadErrorBoundary>
);

function App() {
  useEffect(() => {
    // Fix for iPad PWA mode - prevent default gestures that can cause issues
    const handleGestureStart = (e: Event) => {
      e.preventDefault();
    };
    
    // Fix for orientation changes in PWA mode
    const handleOrientationChange = () => {
      // Force layout recalculation
      window.scrollTo(0, 0);
      // Quick display toggle to force redraw
      const originalDisplay = document.body.style.display;
      document.body.style.display = 'none';
      setTimeout(() => {
        document.body.style.display = originalDisplay;
      }, 20);
    };
    
    document.addEventListener('gesturestart', handleGestureStart);
    window.addEventListener('orientationchange', handleOrientationChange);
    
    // Check if running in standalone PWA mode
    const isInStandaloneMode = window.matchMedia('(display-mode: standalone)').matches || 
                               (window.navigator as any).standalone;
    
    if (isInStandaloneMode) {
      console.log('Running in PWA standalone mode');
      // Apply any additional PWA-specific tweaks here
      document.documentElement.classList.add('pwa-standalone');
    }
    
    return () => {
      document.removeEventListener('gesturestart', handleGestureStart);
      window.removeEventListener('orientationchange', handleOrientationChange);
    };
  }, []);

  // Initialize WhatsApp background processor
  useEffect(() => {
    import('./services/whatsapp/backgroundProcessor').then(({ whatsappBackgroundProcessor }) => {
      console.log('🚀 Starting WhatsApp background processor...');
      whatsappBackgroundProcessor.start();

      // Cleanup on unmount
      return () => {
        whatsappBackgroundProcessor.stop();
      };
    }).catch(error => {
      console.error('Failed to load WhatsApp background processor:', error);
    });
  }, []);

  return (
    <ThemeProvider theme={theme}>
      <SnackbarProvider 
        maxSnack={3} 
        autoHideDuration={3000}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <CssBaseline />
        <AuthProvider>
          <SettingsProvider>
            <Router>
              <PWAInstallPrompt />
              <UpdateNotification />
              <NetworkStatus />
              <RoutePreloader />
              <Routes>
                <Route path="/" element={<Login />} />
                <Route path="/photo-upload" element={
                  <Suspense fallback={<LoadingFallback />}>
                    <PhotoUploadPage />
                  </Suspense>
                } />
                <Route path="/appointments/create" element={
                  <Suspense fallback={<LoadingFallback />}>
                    <PublicAppointmentCreate />
                  </Suspense>
                } />
                <Route element={<PrivateRoute />}>
                  <Route path="/dashboard" element={<Navigate to="/pos" replace />} />
                  <Route path="/inventory" element={
                    <Suspense fallback={<LoadingFallback />}>
                      <Inventory />
                    </Suspense>
                  } />
                  <Route path="/services" element={
                    <Suspense fallback={<LoadingFallback />}>
                      <Services />
                    </Suspense>
                  } />
                  <Route path="/packages" element={
                    <LazyRoute>
                      <Packages />
                    </LazyRoute>
                  } />
                  <Route path="/pos" element={
                    <Suspense fallback={<LoadingFallback />}>
                      <POS />
                    </Suspense>
                  } />
                  <Route path="/service-slots" element={
                    <Suspense fallback={<LoadingFallback />}>
                      <ServiceSlots />
                    </Suspense>
                  } />
                  <Route path="/customers" element={
                    <Suspense fallback={<LoadingFallback />}>
                      <Customers />
                    </Suspense>
                  } />
                  <Route path="/customers/:customerId" element={
                    <Suspense fallback={<LoadingFallback />}>
                      <CustomerProfilePage />
                    </Suspense>
                  } />
                  <Route path="/staff" element={<Navigate to="/settings?tab=4" replace />} />
                  <Route path="/finances" element={
                    <LazyRoute>
                      <Finances />
                    </LazyRoute>
                  } />
                  <Route path="/whatsapp" element={
                    <Suspense fallback={<LoadingFallback />}>
                      <WhatsApp />
                    </Suspense>
                  } />
                  <Route path="/whatsapp-test" element={
                    <Suspense fallback={<LoadingFallback />}>
                      <WhatsAppTest />
                    </Suspense>
                  } />
                  <Route path="/settings" element={
                    <Suspense fallback={<LoadingFallback />}>
                      <Settings />
                    </Suspense>
                  } />
                  <Route path="/appointments" element={
                    <Suspense fallback={<LoadingFallback />}>
                      <Appointments />
                    </Suspense>
                  } />
                  <Route path="/general-ledger" element={
                    <Suspense fallback={<LoadingFallback />}>
                      <GeneralLedger />
                    </Suspense>
                  } />
                  <Route path="/notifications" element={
                    <Suspense fallback={<LoadingFallback />}>
                      <NotificationSettings />
                    </Suspense>
                  } />
                  <Route path="/analytics" element={<Navigate to="/pos" replace />} />
                  <Route path="/online-presence" element={<Navigate to="/pos" replace />} />
                  <Route path="/gift-cards" element={<Navigate to="/pos" replace />} />
                </Route>
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </Router>
          </SettingsProvider>
        </AuthProvider>
      </SnackbarProvider>
    </ThemeProvider>
  );
}

export default App;
